/* search-scope.js — one query builder for every way into storefront search.

   Shopify's product index includes the description body, and a plain query
   also gets a broad "related words" expansion: "eagle" returned 379 of the
   ~650 products, "lincoln" 698. The `options[fields]` param is silently
   ignored (on /search and on /search/suggest alike), but field-scoped `q`
   syntax is honoured, so every entry point submits the same scoped form:

     Cowboys    ->  (title:cowboys OR title:cowboy OR tag:cowboys OR tag:cowboy
                     OR product_type:cowboys OR product_type:cowboy)
     Civil War  ->  (title:civil OR ...) AND (title:war OR ...)

   What the results page does NOT do (measured 2026-10-20): `*` wildcards and
   options[prefix]=last have no effect on field-scoped terms, and there is no
   typo tolerance — "gettys", "linclon" and "eagel" all find 0. So:
     - each word carries its singular and plural forms (tags are exact:
       tag:cowboy matches 39 pieces, tag:cowboys 5);
     - filler words ("wall art", "print", "the") are dropped, because every
       word must match and "eagle wall art" found 2 pieces instead of 19;
     - typos and half-words are corrected by correct() below, with
       /search/suggest, which IS typo- and prefix-tolerant: at submit time
       when the dropdown already saw no exact match, otherwise on the
       results page ("Showing results for lincoln (you searched linclon)").

   snippets/search-query.liquid is the Liquid twin of build() (trending chips,
   format chips, and the redirect that scopes a plain ?q= arriving from a
   link). Change one, change the other — they must produce identical strings.
   Without JS the form submits the raw term and the results page redirects. */
(function () {
  'use strict';

  var FIELDS = ['title', 'tag', 'product_type'];
  var FILLER = '|a|an|the|of|and|or|not|for|in|on|to|with|by|my|wall|art|arts|artwork|decor|print|prints|picture|pictures|painting|paintings|';
  var SYNONYMS = { metal: 'aluminum', aluminium: 'aluminum' };
  var MAX_WORDS = 6;

  /* Format and sale chips on the results page append one of these. They are
     written without parentheses so they can never be mistaken for a word
     clause, which always opens with "(". */
  var FILTERS = {
    framed: ' AND product_type:framed',
    canvas: ' AND product_type:canvas',
    poster: ' AND product_type:poster',
    aluminum: ' AND product_type:aluminum',
    sale: ' AND tag:on-sale'
  };

  function trimEdges(w) {
    // A leading "-" reads as negation to Shopify; quotes and dashes at either
    // end are noise. Three passes, like the Liquid twin.
    for (var i = 0; i < 3; i++) {
      if (w.charAt(0) === '-' || w.charAt(0) === "'") w = w.slice(1);
      var c = w.charAt(w.length - 1);
      if (c === '-' || c === "'") w = w.slice(0, -1);
    }
    return w;
  }

  function words(raw) {
    var all = [];
    String(raw == null ? '' : raw).toLowerCase()
      .replace(/[‘’]/g, "'")
      .replace(/[()"“”*~^,.!?;:&\\]/g, ' ')
      .split(/\s+/)
      .forEach(function (w) {
        if (w.slice(-2) === "'s") w = w.slice(0, -2);
        w = trimEdges(w);
        if (w) all.push(w);
      });
    var kept = all.filter(function (w) { return FILLER.indexOf('|' + w + '|') === -1; });
    return (kept.length ? kept : all).slice(0, MAX_WORDS);
  }

  /* The word as typed first (the results page reads it back from there),
     then at most two other singular/plural spellings. Junk spellings such as
     "hors" for "horses" cost nothing: a term only matches a whole token. */
  function forms(w) {
    var out = [w];
    var n = w.length;
    if (/^[0-9]+$/.test(w) || n < 3) return out;
    if (w.slice(-3) === 'ies' && n > 4) out.push(w.slice(0, -3) + 'y');
    else if (w.slice(-2) === 'es' && n > 4) out.push(w.slice(0, -1), w.slice(0, -2));
    else if (w.slice(-2) === 'ss') out.push(w + 'es');
    else if (w.slice(-1) === 's' && n > 3) out.push(w.slice(0, -1));
    else if (w.slice(-1) === 'y') out.push(w + 's', w.slice(0, -1) + 'ies');
    else if (w.slice(-1) !== 's') out.push(w + 's');
    return out;
  }

  /* Raw shopper text -> scoped query. Optional filter keys from FILTERS.
     Returns '' when nothing usable is left, and callers then fall back to the
     raw term rather than submitting a broken query. */
  function build(raw, filters) {
    var ws = words(raw);
    if (!ws.length) return '';
    var q = ws.map(function (w) {
      var f = forms(SYNONYMS[w] || w);
      var terms = [];
      FIELDS.forEach(function (field) { f.forEach(function (x) { terms.push(field + ':' + x); }); });
      return '(' + terms.join(' OR ') + ')';
    }).join(' AND ');
    (filters || []).forEach(function (k) { if (FILTERS[k]) q += FILTERS[k]; });
    return q;
  }

  /* Text the shopper wrote with search syntax of their own is passed through
     untouched: re-scoping it would corrupt it. Syntax means a field prefix at
     the start of a word (after a space, "(", "-" or a quote; any case), or
     parentheses together with an upper-case AND / OR / NOT. A plain colon
     ("Washington: Father of a Nation", pasted from a title) is not syntax,
     and neither are capitals alone: "STARS AND STRIPES" and "DO NOT TREAD ON
     ME" used to skip the scoping and list the whole catalogue (864 / 863).
     sections/main-search.liquid applies the same rule (is_syntax) to a ?q=
     arriving from a link; keep the two in step. */
  var SYNTAX_FIELDS = ['title', 'tag', 'product_type', 'vendor', 'body', 'sku'];
  function isAlreadyScoped(raw) {
    raw = String(raw == null ? '' : raw);
    var probe = ' ' + raw.toLowerCase().replace(/[(\-"]/g, ' ');
    if (SYNTAX_FIELDS.some(function (f) { return probe.indexOf(' ' + f + ':') !== -1; })) return true;
    if (!/[()]/.test(raw)) return false;
    return / (AND|OR|NOT) /.test(' ' + raw.replace(/[()]/g, ' ') + ' ');
  }

  /* Scoped query -> the words to show the shopper (twin of the unwind mode of
     snippets/search-query.liquid). */
  function unwind(q) {
    q = String(q || '');
    if (q.indexOf('title:') === -1) return q;
    Object.keys(FILTERS).forEach(function (k) { q = q.split(FILTERS[k]).join(''); });
    return q.replace(/[()]/g, '').split(' AND ').map(function (clause) {
      return clause.split(' OR ')[0].split(':').pop().replace(/\*/g, '').trim();
    }).join(' ').trim();
  }

  function searchRoute() {
    var t = window.theme || {};
    return (t.routes && t.routes.search) || '/search';
  }

  /* The results-page URL for a raw term — what the form, the dropdown's
     "See all", recent searches and suggestion chips all link to. */
  function url(raw) {
    var q = isAlreadyScoped(raw) ? raw : (build(raw) || raw);
    return searchRoute() + '?q=' + encodeURIComponent(q) + '&type=product';
  }

  /* ---- recent searches (this browser only) ---- */
  var RECENT_KEY = 'coa-recent-searches';
  // Until 2026-10-20 the results page saved Shopify's HTML-escaped terms
  // ("don&#39;t tread me"); read those back as the shopper typed them.
  function unescapeOld(s) {
    return s.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
  }
  var recent = {
    get: function () {
      try {
        var list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        return Array.isArray(list) ? list.filter(function (s) { return typeof s === 'string' && s; }).map(unescapeOld).slice(0, 5) : [];
      } catch (e) { return []; }
    },
    add: function (term) {
      term = String(term || '').trim();
      if (term.length < 2 || term.length > 60) return;
      var lower = term.toLowerCase();
      var list = recent.get().filter(function (s) { return s.toLowerCase() !== lower; });
      list.unshift(term);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5))); } catch (e) {}
    },
    clear: function () { try { localStorage.removeItem(RECENT_KEY); } catch (e) {} }
  };

  /* ---- spelling correction ----
     The scoped search has no typo tolerance; /search/suggest has. correct()
     finds the term a misspelled or half-typed search meant ("linclon" ->
     lincoln, "eagel" -> eagle, "gett" -> gettysburg) and only returns one
     whose own scoped search has results. Used at submit time (below) and by
     the results page when a link or a fast Enter lands on nothing. */

  // Optimal-string-alignment distance: "eagel" -> "eagle" is 1 (a swap).
  function distance(a, b) {
    var d = [], i, j;
    for (i = 0; i <= a.length; i++) { d[i] = [i]; }
    for (j = 0; j <= b.length; j++) { d[0][j] = j; }
    for (i = 1; i <= a.length; i++) {
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
      }
    }
    return d[a.length][b.length];
  }
  function allowed(w) { return w.length <= 4 ? 1 : (w.length <= 8 ? 2 : 3); }
  // A typed word "is" a candidate word when it is a close misspelling of it,
  // or the start of it (a half-typed "gett" for "gettysburg").
  function near(w, c) { return (w.length >= 3 && c.indexOf(w) === 0) || distance(w, c) <= allowed(w); }
  function tokens(s) {
    return String(s || '').toLowerCase().replace(/[‘’]/g, "'").split(/[^a-z0-9'-]+/)
      .map(function (t) { return t.replace(/'s$/, '').replace(/^[-']+|[-']+$/g, ''); })
      .filter(function (t) { return t.length > 1; });
  }

  /* Shopify's own corrections only count when every typed word is close to
     one of their words, so an unrelated "popular" suggestion can't hijack a
     search. */
  function closeEnough(raw, cand) {
    var cw = tokens(cand);
    return words(raw).every(function (w) { return cw.some(function (c) { return near(w, c); }); });
  }

  /* No usable suggestion ("eagel" got none): fix each word against the words
     in the titles Shopify's fuzzy search did return. */
  function fixFromTitles(raw, titles) {
    var freq = {};
    titles.forEach(function (t) { tokens(t).forEach(function (k) { freq[k] = (freq[k] || 0) + 1; }); });
    var vocab = Object.keys(freq);
    var changed = false;
    var fixed = words(raw).map(function (w) {
      if (forms(w).some(function (f) { return freq[f]; })) return w;
      var best = null, bestScore = Infinity;
      vocab.forEach(function (k) {
        if (!near(w, k)) return;
        var score = (k.indexOf(w) === 0 ? 0 : distance(w, k)) * 100 - freq[k];
        if (score < bestScore) { best = k; bestScore = score; }
      });
      if (best) { changed = true; return best; }
      return w;
    });
    return changed ? fixed.join(' ') : null;
  }

  var counts = {};
  function count(q) {
    if (!counts[q]) {
      counts[q] = fetch(searchRoute() + '?view=count&type=product&q=' + encodeURIComponent(q))
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (t) { var n = parseInt(String(t).trim(), 10); return isNaN(n) ? null : n; })
        .catch(function () { return null; });
    }
    return counts[q];
  }

  /* /search/suggest, cached per URL for the page's lifetime. Shared with
     predictive-search.js, so a correction at submit time reuses the answer
     the dropdown already has instead of asking again. */
  var suggestCache = {};
  function suggest(q, types, signal) {
    var t = window.theme || {};
    var url = ((t.routes && t.routes.predictiveSearch) || '/search/suggest') +
      '.json?q=' + encodeURIComponent(q) + '&resources[type]=' + types +
      '&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last';
    if (suggestCache[url]) return Promise.resolve(suggestCache[url]);
    return fetch(url, signal ? { signal: signal } : undefined)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var r = (data.resources && data.resources.results) || {};
        var out = { products: r.products || [], collections: r.collections || [], queries: r.queries || [] };
        suggestCache[url] = out;
        return out;
      });
  }

  function correct(raw) {
    return suggest(raw, 'query,product,collection').then(function (res) {
      var lower = String(raw).trim().toLowerCase();
      var cands = [];
      function add(c) {
        c = String(c || '').trim().toLowerCase();
        if (c && c !== lower && cands.indexOf(c) === -1) cands.push(c);
      }
      (res.queries || []).forEach(function (q) { if (closeEnough(raw, q.text)) add(q.text); });
      var titles = (res.products || []).map(function (p) { return p.title; })
        .concat((res.collections || []).map(function (c) { return c.title; }));
      add(fixFromTitles(raw, titles));
      cands = cands.slice(0, 4);
      if (!cands.length) return null;
      // First candidate (in Shopify's order) whose own results aren't empty.
      return Promise.all(cands.map(function (c) { return count(build(c)); })).then(function (ns) {
        for (var i = 0; i < cands.length; i++) { if (ns[i] > 0) return cands[i]; }
        return null;
      });
    });
  }

  function scopeForm(form) {
    // Back from the results page (bfcache): the form must not stay "busy".
    window.addEventListener('pageshow', function () { form.removeAttribute('aria-busy'); });
    form.addEventListener('submit', function (e) {
      var input = form.querySelector('input[name="q"]');
      if (!input) return;
      // Products only, as the results grid renders nothing else. Without it
      // the header bar counted pages and articles ("20 results", 19 cards).
      if (!form.querySelector('input[name="type"]')) {
        var t = document.createElement('input');
        t.type = 'hidden'; t.name = 'type'; t.value = 'product';
        form.appendChild(t);
      }
      var raw = input.value.trim();
      if (!raw || isAlreadyScoped(raw)) return;
      /* The suggestions already showed that these exact words find nothing
         (predictive-search.js marks the input): look for the correction
         here and go straight to it, instead of loading an empty results page
         that would do the same and then redirect (3.5s for one typo). */
      if (input.getAttribute('data-no-exact') === raw.toLowerCase() && window.fetch) {
        e.preventDefault();
        if (form.getAttribute('aria-busy') === 'true') return;
        form.setAttribute('aria-busy', 'true');
        var gone = false;
        var go = function (href) { if (!gone) { gone = true; window.location.href = href; } };
        setTimeout(function () { go(url(raw)); }, 2500);
        correct(raw).then(function (fix) {
          go(fix ? url(fix) + '&corrected_from=' + encodeURIComponent(raw) : url(raw) + '&nocorrect=1');
        }, function () { go(url(raw)); });
        return;
      }
      var scoped = build(raw);
      if (!scoped) return;
      input.value = scoped;
      // The form serialises synchronously, so putting the human term back on
      // the next tick keeps the raw text in the box if the page is restored
      // from bfcache instead of leaving the scoped expression on screen.
      setTimeout(function () { input.value = raw; }, 0);
    });
  }

  function init() {
    document.querySelectorAll('form[role="search"]').forEach(scopeForm);
  }

  window.theme = window.theme || {};
  window.theme.searchScope = {
    build: build, words: words, forms: forms, isAlreadyScoped: isAlreadyScoped,
    unwind: unwind, url: url, filters: FILTERS, recent: recent,
    correct: correct, count: count, tokens: tokens, suggest: suggest
  };

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); }
  else { init(); }
})();
